import type { StateCreator } from 'zustand';
import type { StandardLoanState } from '../standardLoanState';
import {
    defaultLoanStepContext,
    emptyLoanForm,
    LoanForm,
    LoanStepContext,
} from '../../components/Steps/components/Loan/LoanInfo/constants/loanForm';
import { applySameAsCommitted } from '../../components/Steps/components/Loan/LoanInfo/functions/applySameAsCommitted';
import { getDefaultInterestAccrualType } from '../../components/Steps/components/Loan/LoanInfo/functions/getDefaultInterestAccrualType';
import { getSameAsCommitted } from '../../components/Steps/components/Loan/LoanInfo/functions/getSameAsCommitted';
import { LoanType } from '../../../../../common/constants/LoanType';
import type { LoanFile } from '../../../../../common/components/v2/LoanFilesTable/constants/loanFile';

/** The Loan step: its fields, and what it needs to know about the loan being added/edited. */
export interface LoanSlice {
    /** The Loan step's fields. */
    loan: LoanForm;
    /** Merges changes into the Loan form; keeps Bridge amounts in step with Prom. Note/Com. when "Same as Committed". */
    updateLoan: (changes: Partial<LoanForm>) => void;
    /**
     * Bridge loans only: Apprvd Loan Amt and Bridge Funded follow Prom. Note/Com. while checked. Screen state only (the
     * loan saves the three amounts), so it's kept out of LoanForm.
     */
    sameAsCommitted: boolean;
    /** Checking it copies Prom. Note/Com. into Apprvd Loan Amt and Bridge Funded; unchecking keeps the amounts. */
    setSameAsCommitted: (sameAsCommitted: boolean) => void;
    /**
     * Called by updateSetup when the Loan Type changes: fills in the default Interest Accrual (if none has been chosen)
     * and works out "Same as Committed" fresh, so switching to Bridge doesn't lock amounts that don't match.
     */
    applyLoanTypeDefaults: (loanTypeId: LoanType | undefined) => void;

    /** The Loan step's Files section: the loan's files, with any added or marked for deletion since it was loaded. */
    loanFiles: LoanFile[];
    setLoanFiles: (loanFiles: LoanFile[]) => void;

    /** Set by whoever opens the modal. */
    loanStepContext: LoanStepContext;
    setLoanStepContext: (context: Partial<LoanStepContext>) => void;
}

/** The Loan slice's starting values; reset() puts these back. */
export const initialLoanState = {
    loan: emptyLoanForm,
    // Unchecked until a Loan Type is picked; applyLoanTypeDefaults then works it out, so a new Bridge loan starts checked
    sameAsCommitted: false,
    loanFiles: [] as LoanFile[],
    loanStepContext: defaultLoanStepContext,
};

export const createLoanSlice: StateCreator<StandardLoanState, [], [], LoanSlice> = (set) => ({
    ...initialLoanState,

    updateLoan: (changes) =>
        set((state) => {
            const loan = { ...state.loan, ...changes };
            if ('promissoryNote' in changes) {
                const isBridgeLoan = state.setup.loanTypeId === LoanType.Bridge;
                Object.assign(loan, applySameAsCommitted(loan, isBridgeLoan, state.sameAsCommitted));
            }
            return { loan };
        }),

    setSameAsCommitted: (sameAsCommitted) =>
        set((state) => {
            if (!sameAsCommitted) {
                return { sameAsCommitted };
            }
            const isBridgeLoan = state.setup.loanTypeId === LoanType.Bridge;
            return {
                sameAsCommitted,
                loan: { ...state.loan, ...applySameAsCommitted(state.loan, isBridgeLoan, sameAsCommitted) },
            };
        }),

    applyLoanTypeDefaults: (loanTypeId) =>
        set((state) => {
            const loan =
                state.loan.interestAcrualTypeId === undefined
                    ? { ...state.loan, interestAcrualTypeId: getDefaultInterestAccrualType(loanTypeId) }
                    : state.loan;
            return { loan, sameAsCommitted: getSameAsCommitted(loanTypeId, loan) };
        }),

    setLoanFiles: (loanFiles) => set({ loanFiles }),

    setLoanStepContext: (context) => set((state) => ({ loanStepContext: { ...state.loanStepContext, ...context } })),
});
